import {
    processGetManyFilter,
    processFilters, addSortToUrl, processGetManyByReferenceFilter, readJsonOrRefuse, refuseOnHttpError} from "./provider/defaults";
import {
    CreateParams,
    CreateResult,
    DataProvider,
    DeleteManyParams,
    DeleteParams, DeleteResult,
    GetListParams,
    GetListResult,
    GetManyParams,
    GetManyReferenceParams,
    GetManyReferenceResult,
    GetManyResult,
    GetOneParams,
    GetOneResult,
    UpdateManyParams,
    UpdateParams,
    UpdateResult
} from "react-admin";
import { read_cookie, delete_cookie } from 'sfcookies';
import { hostUrl, UseUrlManagerCleanUrl, apiUrl } from "../components/utils/url-manager";
import { removeUrlFromUri, restoreEntityString, cleanEntityString } from "./provider/defaults";
import {Company, Workflow} from "../types/entities";

function cleanParams(company: any[]): Company {
    let companiesHouse: string = company['companiesHouse'] ?? '';
    companiesHouse = companiesHouse.replace('/hq_api/companies_houses/', '');
    let addresses: (string[]|null) = company['addresses'];
    if (addresses.length <= 0) {
        addresses = undefined;
    }
    if (Array.isArray(addresses)) {
        let newAddresses: any[] = [];
        addresses.forEach(address => {
            // The leading slash matters. Without it the id came out as `/7`, the addresses
            // request went out as `id[]=/7`, and the Addresses card was empty on every company
            // that had one. 2026-09-10.
            let tmp = address.replace('/hq_api/addresses/', '');
            newAddresses.push(tmp);
        });
        if (newAddresses.length > 0) {
            addresses = newAddresses;
        }
    }

    return {
        id: company['id'],
        name: company['name'],
        yearEstablished: company['yearEstablished'],
        sicCode: company['sicCode'],
        industry: company['industry'],
        // **No `?? 'N/A'` on either of these.** It was here until 2026-09-10, and because the
        // update below PATCHes the whole cleaned record back, a company with no VAT number was
        // saved with the literal string 'N/A' in the column the first time anybody touched the
        // form. An empty field says "not known" without writing anything.
        vat: company['vat'],
        companyNumber: company['companyNumber'],
        companiesHouse: companiesHouse,
        addresses: addresses,
        emails: removeUrlFromUri(company['emails'], '/hq_api/emails/'),
        telephones: removeUrlFromUri(company['telephoneNumbers'], '/hq_api/telephones/'),
        employees: removeUrlFromUri(company['employees'], '/hq_api/employees/'),
        socials: removeUrlFromUri(company['socialMedia'], '/hq_api/social_media/'),
        websites: removeUrlFromUri(company['websites'], '/hq_api/websites/'),
        customerAccount: cleanEntityString(company['customerAccount'], '/hq_api/customer_accounts/'),
        domainEmail: company['domainEmail'],
        verifiedDomainEmail: company['verifiedDomainEmail'],
        // Seven columns the API has always answered with and HQ dropped on the floor until
        // 2026-09-10, when the company Show and Edit screens were merged and there was somewhere
        // to put them. The two campaign collections are read here and **stripped before every
        // write** (see `create` and `update`): they are lists to look at, and PATCHing stale
        // IRIs back at a collection the screen cannot edit is a 400 waiting to happen.
        tradingName: company['tradingName'],
        numberOfEmployees: company['numberOfEmployees'],
        yearsInBusiness: company['yearsInBusiness'],
        logo: company['logo'],
        privacyPolicyUrl: company['privacyPolicyUrl'],
        mysteryShoppers: removeUrlFromUri(company['mysteryShoppers'], '/hq_api/mystery_shoppers/'),
        complianceSniffers: removeUrlFromUri(company['complianceSniffers'], '/hq_api/compliance_sniffers/'),
    }
}

/**
 * What a company write actually sends.
 *
 * **Saving a company from HQ was refused outright until 2026-09-10**, and had been for as long as
 * the screen has existed. The old payload was the whole cleaned record with a prefix pasted onto
 * every collection, which put three kinds of invalid IRI in front of API Platform:
 * `/hq_api/adddresses//7` from a misspelt prefix and a half-stripped id, a bare `12` for
 * `companiesHouse`, and an empty string for the same field on a company that has no Companies
 * House record. Any one of them answers **400 Invalid IRI**, and the error response carries no
 * CORS header, so the browser reported `TypeError: Failed to fetch` and HQ showed nothing at all.
 * Found by watching the PATCH while merging Show into Edit.
 *
 * So the payload is built rather than filtered, and it carries only what this screen edits.
 * Addresses, emails, telephone numbers, employees, social media and websites each have their own
 * screen and their own `company` field; a company write never touched them and PATCHing them back
 * could only overwrite a collection with a stale copy of itself. The two campaign collections are
 * read for their counts and never written for the same reason.
 *
 * A merge patch leaves out what it does not carry, so a field absent here keeps its value.
 */
export function companyWritePayload(data: any): any {
    const payload: any = {
        name: emptyToNull(data.name),
        tradingName: emptyToNull(data.tradingName),
        companyNumber: emptyToNull(data.companyNumber),
        vat: emptyToNull(data.vat),
        sicCode: emptyToNull(data.sicCode),
        industry: emptyToNull(data.industry),
        yearEstablished: emptyToNull(data.yearEstablished),
        yearsInBusiness: emptyToNull(data.yearsInBusiness),
        privacyPolicyUrl: emptyToNull(data.privacyPolicyUrl),
        numberOfEmployees: numberOrNull(data.numberOfEmployees),
    };

    // Both relations are sent as IRIs and **only when they are set**. `setCompaniesHouse()` does
    // not accept null, so a company with no Companies House record leaves the key out rather than
    // clearing it; that is also what stops an empty string reaching the normaliser.
    const account: string|undefined = restoreEntityString(data.customerAccount, '/hq_api/customer_accounts/');
    if (typeof data.customerAccount === 'string' && data.customerAccount !== '' && account !== undefined) {
        payload.customerAccount = account;
    }
    const companiesHouse: string|undefined = restoreEntityString(data.companiesHouse, '/hq_api/companies_houses/');
    if (typeof data.companiesHouse === 'string' && data.companiesHouse !== '' && companiesHouse !== undefined) {
        payload.companiesHouse = companiesHouse;
    }

    return payload;
}

/** An emptied field means "not known", which is null in the column rather than an empty string. */
function emptyToNull(value: any): any {
    if (value === undefined || value === null) {
        return null;
    }

    return typeof value === 'string' && value.trim() === '' ? null : value;
}

/** `numberOfEmployees` is a nullable int, and an emptied NumberInput hands over an empty string. */
function numberOrNull(value: any): number|null {
    if (value === undefined || value === null || value === '') {
        return null;
    }
    const parsed: number = Number(value);

    return Number.isFinite(parsed) ? parsed : null;
}

const customerAccountCompanyProvider: DataProvider = {

    getList:  async  (resource: string, params: GetListParams): Promise<GetListResult> => {
        let token: string = read_cookie('auth');
        let url: string = apiUrl + resource;
        if (params.pagination) {
            url += '?page=' + params.pagination.page + '&itemsPerPage=' + params.pagination.perPage;
        }
        url = processFilters(url, params);
        url = addSortToUrl(url, params);
        url = UseUrlManagerCleanUrl({ url: url });
        const request: Request = new Request(url, {
            method: 'GET',
            headers: new Headers({
                'Content-Type' : 'application/json',
                'token' : token,
                'hq': 'hq'
            })
        });
        return fetch(request)
            .then( (response: Response): Promise<any> => {
                return readJsonOrRefuse(response);
            })
            .then( (data: any): { data: Company[], total: number } => {
                let responseData: { data: Company[], total: number } = {
                    data: [],
                    total: 0
                };
                if (Array.isArray(data['hydra:member'])) {
                    let companies: any[] = data['hydra:member'];
                    let totalItems: number = data['hydra:totalItems'];

                    for(let i: number = 0; i < companies.length; i++) {
                        let company: any[] = companies[i];

                        responseData.data.push(cleanParams(company));
                    }
                    responseData.total = totalItems;
                }
                return responseData;
            })
            .catch( (error: Error): Promise<GetListResult> => {
                console.log(error);
                return Promise.reject(error)
            });
    },
    // get a single record by id
    getOne: async (resource: string, params: GetOneParams) : Promise<GetOneResult> => {
        let token: string = read_cookie('auth');
        let url: string = apiUrl + resource + '/' + params.id;
        url = UseUrlManagerCleanUrl({ url: url });
        const request: Request = new Request(url, {
            method: 'GET',
            headers: new Headers({
                'Content-Type' : 'application/json',
                'token' : token,
                'hq': 'hq'
            })
        });
        return fetch(request)
            .then( (response: Response): Promise<any> => {
                return readJsonOrRefuse(response);
            })
            .then( (data: any): { data: Company } => {
                let responseData: { data: Company|null } = {
                    data: null
                };
                if (data['id']) {
                    responseData.data = cleanParams(data);
                }
                return responseData;
            })
            .catch( (error: Error) => {
                console.log(error);
                return Promise.reject(error);
            });
    },
    // get a list of records based on an array of ids
    getMany: async (resource: string, params: GetManyParams): Promise<GetManyResult> => {

        let token: string = read_cookie('auth');
        let url: string = apiUrl + resource;
        url = processGetManyFilter(url, params);
        url = addSortToUrl(url, params);
        url = UseUrlManagerCleanUrl({ url: url });
        const request: Request = new Request(url, {
            method: 'GET',
            headers: new Headers({
                'Content-Type' : 'application/json',
                'token' : token,
                'hq': 'hq'
            })
        });
        return fetch(request)
            .then( (response: Response) => {
                return readJsonOrRefuse(response);
            })
            .then( (data: any) => {
                let responseData: { data: Company[], total: number } = {
                    data: [],
                    total: 0
                };

                if (Array.isArray(data['hydra:member'])) {
                    let companies: any[] = data['hydra:member'];
                    let totalItems: number = data['hydra:totalItems'];

                    for(let i: number = 0; i < companies.length; i++) {
                        let company: any[] = companies[i];
                        responseData.data.push(cleanParams(company));
                    }
                    responseData.total = totalItems;
                }
                return responseData;
            })
            .catch( (error: Error) => {
                console.log(error);
                return Promise.reject(error)
            });
    },
    // get the records referenced to another record, e.g. comments for a post
    getManyReference: async (resource: string, params: GetManyReferenceParams) : Promise<GetManyReferenceResult> => {
        let token: string = read_cookie('auth');
        let url: string = apiUrl + resource;
        if (params.pagination) {
            url += '?page=' + params.pagination.page + '&itemsPerPage=' + params.pagination.perPage;
        }
        url = processGetManyByReferenceFilter(url, params);
        url = addSortToUrl(url, params);
        url = UseUrlManagerCleanUrl({ url: url });
        const request: Request = new Request(url, {
            method: 'GET',
            headers: new Headers({
                'Content-Type' : 'application/json',
                'token' : token,
                'hq': 'hq'
            })
        });
        return fetch(request)
            .then( (response: Response): Promise<any> => {
                return readJsonOrRefuse(response);
            })
            .then( (data: any): { data: Company[], total: number } => {
                let responseData: { data: Company[], total: number } = {
                    data: [],
                    total: 0
                };

                if (Array.isArray(data['hydra:member'])) {
                    let operators: any[] = data['hydra:member'];
                    let totalItems: number = data['hydra:totalItems'];

                    for(let i: number = 0; i < operators.length; i++) {
                        let operator: any[] = operators[i];
                        responseData.data.push(cleanParams(operator));
                    }
                    responseData.total = totalItems;
                }
                return responseData;
            })
            .catch( (error: Error): Promise<GetManyReferenceResult> => {
                console.log(error);
                return Promise.reject(error)
            });
    },
    // create a record
    create: async (resource: string, params: CreateParams) : Promise<CreateResult> => {
        let token: string = read_cookie('auth');
        let url: string = apiUrl + resource;
        url = UseUrlManagerCleanUrl({ url: url });
        const request: Request = new Request(url, {
            method: 'POST',
            body: JSON.stringify(companyWritePayload(params['data'])),
            headers: new Headers({
                'Content-Type' : 'application/json',
                'token' : token,
                'hq': 'hq'
            })
        });
        return fetch(request)
            .then( (response: Response) => {
                return readJsonOrRefuse(response);
            })
            .then( (data: any) => {
                let responseData: { data: Company|null } = {
                    data: null
                };
                if (data['id']) {
                    responseData.data = cleanParams(data);
                }
                return responseData;
            })
            .catch( (error: Error) => {
                console.log(error);
                return Promise.reject(error);
            });

    },
    // update a record based on a patch
    update: async (resource: string, params: UpdateParams) : Promise<UpdateResult> => {
        let token: string = read_cookie('auth');

        let url: string = apiUrl + resource + '/' + params.data.id;
        url = UseUrlManagerCleanUrl({ url: url });
        const request: Request = new Request(url, {
            method: 'PATCH',
            body: JSON.stringify(companyWritePayload(params['data'])),
            headers: new Headers({
                'Content-Type' : 'application/merge-patch+json',
                'token' : token,
                'hq': 'hq'
            })
        });
        return fetch(request)
            .then( (response: Response) => {
                return readJsonOrRefuse(response);
            })
            .then( (data: any) => {
                let responseData: { data: Company|null } = {
                    data: null
                };
                if (data['id']) {
                    responseData.data = cleanParams(data);
                }
                return responseData;
            })
            .catch( (error: Error) => {
                console.log(error);
                return Promise.reject(error);
            });
    },
    // update a list of records based on an array of ids and a common patch
    updateMany: async (resource: string, params: UpdateManyParams) : Promise<any> => {
        return Promise.reject();
    },
    // delete a record by id
    delete: async (resource: string, params: DeleteParams) : Promise<DeleteResult> => {
        let token: string = read_cookie('auth');
        let url: string = apiUrl + resource + '/' + params.id;
        url = UseUrlManagerCleanUrl({ url: url });
        const request: Request = new Request(url, {
            method: 'DELETE',
            headers: new Headers({
                'Content-Type' : 'application/json',
                'token' : token,
                'hq': 'hq'
            })
        });
        return fetch(request)
            .then( (response: Response) => {
                // debugger;
                return refuseOnHttpError(response)
                    .then((): { data: any } => ({ data: params }));
            })
            .catch( (error: Error) => {
                console.log(error);
                return Promise.reject(error);
            });
    },
    // delete a list of records based on an array of ids
    deleteMany: async (resource: string, params: DeleteManyParams) => {
        return Promise.reject();
    },
    getProfileData: (params) => {
        return Promise.resolve();
    }
}

export default customerAccountCompanyProvider;